/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Lottie-powered loading fallback displayed while Three.js scenes are loading.
 * Used by ThreeSceneShell as the Suspense fallback for all 3D canvases.
 */

import { useEffect, useMemo, useState, type ComponentType, type CSSProperties } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import sceneLoaderData from '@/assets/scene-loader.json';

interface SceneLoadingFallbackProps {
  message?: string;
  bgcolor?: string;
}

type LottieComponent = ComponentType<{
  animationData: unknown;
  loop?: boolean;
  style?: CSSProperties;
}>;

function isLottieComponent(value: unknown): value is LottieComponent {
  return typeof value === 'function';
}

function resolveLottie(mod: unknown): LottieComponent | null {
  const record = mod && typeof mod === 'object' ? (mod as Record<string, unknown>) : null;
  const nested = record?.default && typeof record.default === 'object'
    ? (record.default as Record<string, unknown>)
    : null;

  const candidates = [
    record?.default,
    nested?.default,
    record?.Lottie,
    nested?.Lottie,
    mod,
  ];

  return candidates.find(isLottieComponent) ?? null;
}

export function SceneLoadingFallback({ message, bgcolor = 'transparent' }: SceneLoadingFallbackProps) {
  const [Lottie, setLottie] = useState<LottieComponent | null>(null);
  const style = useMemo(
    () => ({ width: 120, height: 120, filter: 'brightness(1.4) saturate(0.8)' }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    void import('lottie-react').then((mod) => {
      if (cancelled) return;
      const Comp = resolveLottie(mod);
      if (Comp) setLottie(() => Comp);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor,
      }}
    >
      <Stack spacing={1.5} alignItems="center">
        {Lottie ? (
          <Lottie animationData={sceneLoaderData} loop style={style} />
        ) : (
          <Box sx={{ width: 120, height: 120 }} />
        )}
        {message && (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.45)',
              fontSize: '0.8rem',
              letterSpacing: '0.04em',
            }}
          >
            {message}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
